import { AnimatePresence, motion } from "framer-motion";
import useAnalytics from "hooks/useAnalytics";
import { ConsensusMeterData } from "../types";
import { MeterBars } from "./MeterBars";
import { ResultsSummary } from "./ResultsSummary";
import { ResultsTable } from "./ResultsTable";
import { SnapshotToggleButton } from "./SnapshotToggleButton";

interface MeterContentProps {
  consensusMeterSuccess: boolean;
  consensusMeterResults: ConsensusMeterData | undefined;
  isLoading: boolean;
  hasMeterFailed: boolean;
  isFilteringEnabled: boolean;
  threadInteractionId?: string;
  isSnapshotOpen: boolean;
  setIsSnapshotOpen: (isSnapshotOpen: boolean) => void;
  searchId?: string;
}

/**
 * Component that displays the consensus meter content when data is available
 */
export const MeterContent = ({
  consensusMeterSuccess,
  consensusMeterResults,
  isLoading,
  hasMeterFailed,
  isFilteringEnabled,
  threadInteractionId,
  isSnapshotOpen,
  setIsSnapshotOpen,
  searchId,
}: MeterContentProps) => {
  const { toggleYesNoSummaryTableEvent } = useAnalytics();

  const handleToggleSnapshot = () => {
    toggleYesNoSummaryTableEvent(!isSnapshotOpen);
    setIsSnapshotOpen(!isSnapshotOpen);
  };

  return (
    <div className="@container">
      <div className="mt-6">
        <MeterBars
          categoriesData={consensusMeterResults} // categoriesData is a subset of consensusMeterResults
          numResultsAnalyzed={consensusMeterResults?.num_results_analyzed ?? 0}
          isLoading={isLoading}
          hasMeterFailed={hasMeterFailed}
        />
      </div>

      <AnimatePresence>
        {isSnapshotOpen ? (
          <motion.div
            key="results-table"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.4, ease: "easeInOut" },
              opacity: { duration: 0.2, ease: "easeInOut", delay: 0.15 },
            }}
            style={{ overflow: "hidden" }}
            className="mt-6"
          >
            <ResultsTable
              consensusMeterResults={consensusMeterResults}
              consensusMeterSuccess={consensusMeterSuccess}
              isFilteringEnabled={isFilteringEnabled}
              onToggleSnapshot={handleToggleSnapshot}
              threadInteractionId={threadInteractionId}
              searchId={searchId}
            />
          </motion.div>
        ) : (
          <motion.div
            key="results-summary"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                opacity: { duration: 0.1, delay: 0.3 },
                ease: "easeInOut",
              },
            }}
            exit={{
              opacity: 0,
              transition: {
                opacity: { duration: 0.1 },
                ease: "easeInOut",
              },
            }}
            className={
              "flex justify-between items-end @mobile:items-center mt-6"
            }
          >
            <ResultsSummary
              consensusMeterResults={consensusMeterResults}
              consensusMeterSuccess={consensusMeterSuccess}
              isFilteringEnabled={isFilteringEnabled}
              threadInteractionId={threadInteractionId}
            />
            <SnapshotToggleButton
              isSnapshotOpen={isSnapshotOpen}
              disabled={!consensusMeterSuccess}
              onToggleSnapshot={handleToggleSnapshot}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
